import React, { useRef, useState, useCallback } from "react";
import parse from "html-react-parser";
import {
  findTypeOption,
  Section,
  typeLibro,
} from "../../../interfaces/Publication";
import { usePublication } from "../../../hooks/usePublication";
import { Link, useParams, useLocation } from "react-router-dom";
import { isDesktop } from "react-device-detect";
import { ModalTrivia } from "../RightMenu";
import { useTrivia } from "../../../hooks";

export type LeftMenuSectionProps = {
  section: Section;
  index: number;
  toggleMenu: () => void;
};

const LeftMenuSection = ({
  section,
  index,
  toggleMenu,
}: LeftMenuSectionProps) => {
  let { idRevista, idLibro } = useParams();
  const location = useLocation();
  const typeLibro: typeLibro = idLibro ? "libro" : "revista";
  const route = findTypeOption(typeLibro)?.route || "";
  const { openModalTrivia, setCurrentTriviaId } = useTrivia();

  const { publication } = usePublication();
  const [active, setActive] = useState(false);
  const divRef = useRef<HTMLAnchorElement>(null);

  const alternarVista = () => {
    setActive(!active);
  };

  const onCloseMenu = () => {
    if (isDesktop) return;
    toggleMenu();
  };

  const hasSubsectionTitle = section.subsections?.some(
    (sub) => !!sub.title && sub.title.trim() !== ""
  );

  // Función para manejar click en subsección
  const handleSubsectionClick = useCallback((subsectionId: string, e: React.MouseEvent) => {
    
    const anchorName = `C${subsectionId}`;
    
    // Cerrar menú en móviles
    onCloseMenu();
    
    // Disparar evento para el scroll inteligente DESPUÉS de que React Router haya cambiado la URL
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent('scrollToAnchor', {
          detail: { anchorName }
        })
      );
    }, 100);
  }, [onCloseMenu]);

  //  Función para abrir trivia
  const openTrivia = useCallback((trivia: number) => {
    setCurrentTriviaId(trivia || 0);
    const overlay = document.querySelector(".js-backdrop");
    overlay?.classList.add("visible-fadein");
    openModalTrivia();
  }, []);

  //  Función para abrir modal con query parameter
  const openSubsectionModal = (sectionId: string, subId: string) => {
    // Actualiza la URL agregando query parameter
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("modal", subId);
    searchParams.set("section", sectionId);
    const newUrl = `${location.pathname}?${searchParams.toString()}`;
    window.history.pushState({}, "", newUrl);

    //  Disparar evento personalizado para notificar el cambio
    window.dispatchEvent(
      new CustomEvent("urlModalChange", {
        detail: { modalId: subId },
      })
    );

    // Abrir modal visualmente
    const modal = document.getElementById("modal-fragment");
    const backdrop = document.querySelector(".js-backdrop");
    if (modal && backdrop) {
      modal.classList.add("visible-slideup");
      backdrop.classList.add("visible-fadein");
    }

    // Cierra el menú lateral en móviles
    onCloseMenu();
  };

  return (
    <li className="nav__menu-item">
      <Link
        to={`/${route}/${publication.id}/${index}`}
        className="nav__link"
        ref={divRef}
        onClick={onCloseMenu}
      >
        <span className="nav__icon">{index + 1}</span>
        {parse(section.title)}
      </Link>

      {/* Flecha para expandir subsecciones */}
      <a
        className={`nav__link nav__arrow ${active ? "active" : ""}`}
        onClick={alternarVista}
        style={{
          display: hasSubsectionTitle ? "block" : "none",
        }}
      >
        <div className="nav__arrow"></div>
      </a>

      {/* Submenú con subsecciones */}
      <div className="nav__menu-backdrop">
        <ul className="nav__submenu-lvl2 unstyled-list">
          {section.subsections?.map((subsection, idxSub) => (
            <li
              className="nav__submenu-item"
              key={idxSub}
              onClick={onCloseMenu}
            >
              {subsection.fragments.length > 0 ? (
                // Si es una trivia, abrir ModalTrivia, sino el modal normal
                (subsection.title && subsection.title.toLowerCase() === "trivia") ? (
                  <a
                    className="nav__link"
                    onClick={(e) => {
                      e.preventDefault();
                      openTrivia(section.trivia || 0);
                    }}
                  >
                    {subsection.title}
                  </a>
                ) : (
                  <Link
                    className="nav__link"
                    to={`/${route}/${publication.id}/${index}#C${subsection.id}`}
                    onClick={(e) => handleSubsectionClick(subsection.id.toString(), e)}
                  >
                    {subsection.title}
                  </Link>
                )
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
};

export default LeftMenuSection;